/**
 * Widget Styles - CSS-in-JS
 */

export const WIDGET_ID = 'chuck-widget-container';
export const WIDGET_SHADOW_ID = 'chuck-widget-shadow';

export function injectStyles(headerColor: string, bodyColor: string, textColor: string) {
  return `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    .chuck-widget {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: ${textColor};
    }

    /* Bubble Button */
    .chuck-bubble {
      position: fixed;
      bottom: 20px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: ${headerColor};
      color: white;
      border: none;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s, box-shadow 0.2s;
      z-index: 9999;
    }

    .chuck-bubble:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    .chuck-bubble.left {
      left: 20px;
    }

    .chuck-bubble.right {
      right: 20px;
    }

    .chuck-bubble svg {
      width: 28px;
      height: 28px;
    }

    .chuck-bubble-badge {
      position: absolute;
      top: -5px;
      right: -5px;
      background: #ef4444;
      color: white;
      font-size: 12px;
      font-weight: 600;
      min-width: 20px;
      height: 20px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 6px;
    }

    .chuck-proactive-preview {
      position: fixed;
      bottom: 96px;
      width: min(390px, calc(100vw - 40px));
      color: #1f2937;
      z-index: 9998;
      animation: previewIn 0.2s ease-out;
    }

    .chuck-proactive-preview.left {
      left: 20px;
    }

    .chuck-proactive-preview.right {
      right: 20px;
    }

    .chuck-proactive-preview-card {
      min-height: 88px;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
      display: grid;
      grid-template-columns: 40px minmax(0, 1fr) 36px;
      align-items: center;
      gap: 12px;
      padding: 12px 8px 12px 12px;
    }

    .chuck-proactive-preview-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${headerColor};
      color: #ffffff;
      font-size: 17px;
      font-weight: 700;
    }

    .chuck-proactive-preview-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .chuck-proactive-preview-message {
      min-width: 0;
      padding: 4px 0;
      background: transparent;
      color: inherit;
      border: none;
      cursor: pointer;
      font: inherit;
      text-align: left;
      overflow-wrap: anywhere;
      display: flex;
      flex-direction: column;
      gap: 5px;
      line-height: 1.35;
    }

    .chuck-proactive-preview-name {
      color: #64748b;
      font-size: 13px;
      font-weight: 600;
    }

    .chuck-proactive-preview-message:hover {
      color: ${headerColor};
    }

    .chuck-proactive-preview-message:focus-visible,
    .chuck-proactive-preview-close:focus-visible {
      outline: 2px solid ${headerColor};
      outline-offset: 2px;
    }

    .chuck-proactive-preview-close {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      color: #6b7280;
      border: none;
      border-radius: 50%;
      cursor: pointer;
    }

    .chuck-proactive-preview-close:hover {
      background: #f3f4f6;
      color: #1f2937;
    }

    .chuck-proactive-preview-close svg {
      width: 16px;
      height: 16px;
    }

    .chuck-proactive-preview-replies {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding-top: 10px;
      justify-content: flex-end;
    }

    .chuck-proactive-preview.left .chuck-proactive-preview-replies {
      justify-content: flex-start;
    }

    .chuck-proactive-preview-reply {
      min-height: 40px;
      padding: 8px 14px;
      background: #ffffff;
      color: ${headerColor};
      border: 1px solid ${headerColor};
      border-radius: 8px;
      cursor: pointer;
      font: inherit;
      font-weight: 500;
      transition: background 0.15s, color 0.15s;
    }

    .chuck-proactive-preview-reply:hover {
      background: ${headerColor};
      color: #ffffff;
    }

    .chuck-proactive-preview-reply:focus-visible {
      outline: 2px solid ${headerColor};
      outline-offset: 2px;
    }

    /* Chat Window */
    .chuck-window {
      position: fixed;
      bottom: 90px;
      width: 380px;
      height: 600px;
      max-height: calc(100vh - 120px);
      background: ${bodyColor};
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      z-index: 9998;
      animation: slideIn 0.2s ease-out;
    }

    .chuck-window.left {
      left: 20px;
    }

    .chuck-window.right {
      right: 20px;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes previewIn {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Header */
    .chuck-header {
      background: ${headerColor};
      color: white;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .chuck-header-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .chuck-avatar {
      position: relative;
      width: 40px;
      height: 40px;
      flex-shrink: 0;
    }

    .chuck-avatar-img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.3);
      object-fit: cover;
    }

    .chuck-avatar-initial {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 18px;
      font-weight: 600;
      font-family: Arial, sans-serif;
    }

    .chuck-online-indicator {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 12px;
      height: 12px;
      background: #22c55e;
      border: 2px solid ${headerColor};
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(34, 197, 94, 0);
      }
    }

    .chuck-header-title {
      font-size: 16px;
      font-weight: 600;
    }

    .chuck-header-subtitle {
      font-size: 12px;
      opacity: 0.9;
    }

    .chuck-header-close {
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: background 0.2s;
    }

    .chuck-header-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .chuck-header-close svg {
      width: 20px;
      height: 20px;
    }

    /* Messages Area */
    .chuck-messages {
      flex: 1;
      overflow-y: auto;
      padding: 8px 16px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .chuck-message {
      max-width: 80%;
      padding: 12px 16px;
      border-radius: 16px;
      word-break: break-word;
    }

    .chuck-message.visitor {
      align-self: flex-end;
      background: ${headerColor};
      color: white;
      border-bottom-right-radius: 4px;
    }

    .chuck-message.agent {
      align-self: flex-start;
      background: #f3f4f6;
      color: #1f2937;
      border-bottom-left-radius: 4px;
    }

    .chuck-message.system {
      align-self: center;
      background: transparent;
      color: #6b7280;
      font-size: 12px;
      text-align: center;
      padding: 8px 12px;
    }

    .chuck-message a {
      color: inherit;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .chuck-message-meta {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      opacity: 0.7;
      margin-top: 4px;
    }

    /* Typing Indicator */
    .chuck-typing {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      background: #f3f4f6;
      border-radius: 16px;
      border-bottom-left-radius: 4px;
      max-width: 80%;
      align-self: flex-start;
      color: #6b7280;
      font-size: 13px;
    }

    .chuck-typing-dots {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .chuck-typing-dot {
      width: 8px;
      height: 8px;
      background: ${headerColor};
      border-radius: 50%;
      animation: bounce 1.4s infinite ease-in-out;
    }

    .chuck-typing-dot:nth-child(1) {
      animation-delay: -0.32s;
    }

    .chuck-typing-dot:nth-child(2) {
      animation-delay: -0.16s;
    }

    @keyframes bounce {
      0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.5;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }

    /* Input Area */
    .chuck-input {
      border-top: 1px solid #e5e7eb;
      padding: 12px 16px;
      display: flex;
      gap: 8px;
      background: white;
    }

    .chuck-input-field {
      flex: 1;
      border: 1px solid #e5e7eb;
      border-radius: 24px;
      padding: 10px 16px;
      font-size: 14px;
      outline: none;
      transition: border-color 0.2s;
    }

    .chuck-input-field:focus {
      border-color: ${headerColor};
    }

    .chuck-input-field::placeholder {
      color: #9ca3af;
    }

    .chuck-input-send {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${headerColor};
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s, opacity 0.2s;
    }

    .chuck-input-send:hover {
      transform: scale(1.05);
    }

    .chuck-input-send:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .chuck-input-send svg {
      width: 18px;
      height: 18px;
    }

    /* Loading State */
    .chuck-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: #6b7280;
    }

    .chuck-loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid #e5e7eb;
      border-top-color: ${headerColor};
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Welcome Message */
    .chuck-welcome {
      text-align: center;
      padding: 24px;
      color: #6b7280;
    }

    .chuck-welcome-icon {
      width: 48px;
      height: 48px;
      background: ${headerColor}20;
      color: ${headerColor};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 12px;
    }

    .chuck-welcome-icon svg {
      width: 24px;
      height: 24px;
    }

    .chuck-welcome-text {
      font-size: 14px;
      line-height: 1.5;
    }

    /* Conversation Starters */
    .chuck-starters {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
      margin-top: 16px;
      padding: 0 8px;
    }

    .chuck-starter-btn {
      background: ${headerColor}12;
      color: ${headerColor};
      border: 1px solid ${headerColor}30;
      border-radius: 20px;
      padding: 8px 16px;
      font-size: 13px;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;
      line-height: 1.3;
      white-space: nowrap;
    }

    .chuck-starter-btn:hover {
      background: ${headerColor}22;
      border-color: ${headerColor}50;
      transform: translateY(-1px);
    }

    .chuck-starter-btn:active {
      transform: translateY(0);
    }

    .chuck-starter-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .chuck-starter-answers {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-self: flex-start;
      max-width: 90%;
    }

    /* Transfer Countdown Timer */
    .chuck-transfer-timer {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      margin: 4px 0;
      font-size: 12px;
      color: #6b7280;
      background: #f3f4f6;
      border-radius: 12px;
      width: fit-content;
    }

    .chuck-transfer-timer-icon {
      display: flex;
      align-items: center;
      color: ${headerColor};
      animation: pulse-subtle 2s ease-in-out infinite;
    }

    @keyframes pulse-subtle {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    /* Powered By */
    .chuck-powered {
      text-align: center;
      padding: 8px;
      font-size: 11px;
      color: #9ca3af;
    }

    .chuck-powered a {
      color: inherit;
      text-decoration: none;
    }

    .chuck-powered a:hover {
      text-decoration: underline;
    }

    /* Header Actions */
    .chuck-header-actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .chuck-header-menu,
    .chuck-header-back {
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 6px;
      border-radius: 6px;
      transition: background 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .chuck-header-menu:hover,
    .chuck-header-back:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .chuck-header-menu svg,
    .chuck-header-back svg {
      width: 20px;
      height: 20px;
    }

    /* Conversations List */
    .chuck-conversations {
      flex: 1;
      overflow-y: auto;
      padding: 12px;
    }

    .chuck-new-conversation {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 14px 16px;
      background: ${headerColor}10;
      border: 1px dashed ${headerColor}40;
      border-radius: 12px;
      cursor: pointer;
      margin-bottom: 12px;
      font-size: 14px;
      color: ${headerColor};
      transition: all 0.2s;
    }

    .chuck-new-conversation:hover {
      background: ${headerColor}20;
      border-style: solid;
    }

    .chuck-new-conversation svg {
      width: 18px;
      height: 18px;
    }

    .chuck-conversation-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .chuck-conversation-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 14px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      cursor: pointer;
      text-align: left;
      transition: all 0.2s;
    }

    .chuck-conversation-item:hover {
      border-color: ${headerColor}40;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .chuck-conversation-item.active {
      border-color: ${headerColor};
      background: ${headerColor}05;
    }

    .chuck-conversation-preview {
      flex: 1;
      min-width: 0;
    }

    .chuck-conversation-date {
      font-size: 11px;
      color: #9ca3af;
      margin-bottom: 4px;
      display: block;
    }

    .chuck-conversation-text {
      font-size: 13px;
      color: #4b5563;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0;
    }

    .chuck-conversation-badge {
      background: ${headerColor};
      color: white;
      font-size: 11px;
      font-weight: 600;
      min-width: 20px;
      height: 20px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 6px;
      margin-left: 10px;
    }

    .chuck-empty {
      text-align: center;
      padding: 40px 20px;
      color: #9ca3af;
      font-size: 14px;
    }

    /* Input Area Wrapper */
    .chuck-input-area {
      border-top: 1px solid #e5e7eb;
      background: white;
    }

    /* Request Human Button */
    .chuck-request-human {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 100%;
      padding: 10px;
      background: transparent;
      border: none;
      border-top: 1px solid #e5e7eb;
      cursor: pointer;
      font-size: 13px;
      color: #6b7280;
      transition: all 0.2s;
    }

    .chuck-request-human:hover {
      background: #f9fafb;
      color: ${headerColor};
    }

    .chuck-request-human svg {
      width: 16px;
      height: 16px;
    }

    /* Image Upload Button */
    .chuck-input-image {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: transparent;
      color: #374151;
      border: 1px solid #d1d5db;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      flex-shrink: 0;
    }

    .chuck-input-image:hover {
      background: #f3f4f6;
      color: ${headerColor};
      border-color: ${headerColor};
    }

    .chuck-input-image:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .chuck-input-image svg {
      width: 18px;
      height: 18px;
    }

    /* Image Preview */
    .chuck-image-preview {
      position: relative;
      display: inline-block;
      margin: 8px 16px;
    }

    .chuck-image-preview img {
      max-height: 80px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }

    .chuck-image-preview-close {
      position: absolute;
      top: -8px;
      right: -8px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #ef4444;
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }

    .chuck-image-preview-close svg {
      width: 12px;
      height: 12px;
    }

    /* Message Image */
    .chuck-message-image {
      position: relative;
      cursor: pointer;
      display: inline-block;
    }

    .chuck-message-image img {
      max-width: 200px;
      max-height: 150px;
      border-radius: 8px;
      object-fit: cover;
    }

    .chuck-image-unavailable {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 160px;
      min-height: 90px;
      padding: 12px;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.06);
      color: #6b7280;
      font-size: 12px;
    }

    .chuck-message-image-expand {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .chuck-message-image:hover .chuck-message-image-expand {
      opacity: 1;
    }

    .chuck-message-image-expand svg {
      width: 14px;
      height: 14px;
    }

    /* Message Caption (for images with text) */
    .chuck-message-caption {
      margin-top: 8px;
      font-size: 14px;
      line-height: 1.4;
    }

    /* Image Modal */
    .chuck-image-modal {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.85);
      z-index: 10001;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    .chuck-image-modal-content {
      position: relative;
      max-width: 90%;
      max-height: 90%;
    }

    .chuck-image-modal-content img {
      max-width: 100%;
      max-height: 85vh;
      border-radius: 8px;
      object-fit: contain;
    }

    .chuck-image-modal-close {
      position: absolute;
      top: -40px;
      right: 0;
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 8px;
    }

    .chuck-image-modal-close svg {
      width: 24px;
      height: 24px;
    }

    /* =====================================================
       MOBILE CLASS (JavaScript-based fallback)
       ===================================================== */
    .chuck-window.mobile {
      position: fixed;
      top: 0;
      left: 0 !important;
      right: 0 !important;
      bottom: 0;
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
      z-index: 10000;
    }

    .chuck-window.mobile .chuck-header {
      padding: 14px 16px;
      padding-top: max(14px, env(safe-area-inset-top));
    }

    .chuck-window.mobile .chuck-messages {
      padding: 12px;
      gap: 10px;
    }

    .chuck-window.mobile .chuck-message {
      max-width: 85%;
      padding: 10px 14px;
      font-size: 14px;
    }

    .chuck-window.mobile .chuck-input {
      padding: 10px 12px;
      padding-bottom: max(10px, env(safe-area-inset-bottom));
    }

    .chuck-window.mobile .chuck-input-field {
      font-size: 16px; /* Prevents iOS zoom */
    }

    .chuck-window.mobile .chuck-powered {
      padding-bottom: max(8px, env(safe-area-inset-bottom));
    }

    /* =====================================================
       RESPONSIVE DESIGN (Media Query based)
       ===================================================== */

    /* Large desktop (1200px+) - bigger widget */
    @media (min-width: 1200px) {
      .chuck-window {
        width: 420px;
        height: 680px;
      }
    }

    /* Tablet landscape (768px - 1024px) */
    @media (min-width: 768px) and (max-width: 1024px) {
      .chuck-window {
        width: 380px;
        height: 600px;
      }
    }

    /* Tablet portrait and small laptops (600px - 767px) */
    @media (min-width: 600px) and (max-width: 767px) {
      .chuck-window {
        width: 360px;
        height: 550px;
        bottom: 85px;
      }

      .chuck-bubble {
        width: 56px;
        height: 56px;
      }

      .chuck-bubble svg {
        width: 26px;
        height: 26px;
      }
    }

    /* Mobile landscape (480px - 599px) */
    @media (min-width: 480px) and (max-width: 599px) {
      .chuck-window {
        width: 340px;
        height: 500px;
        bottom: 80px;
      }

      .chuck-header {
        padding: 14px 16px;
      }

      .chuck-messages {
        padding: 12px;
        gap: 10px;
      }

      .chuck-message {
        padding: 10px 14px;
        font-size: 13px;
      }
    }

    /* Mobile portrait (up to 479px) - FULL SCREEN */
    @media (max-width: 479px) {
      .chuck-window {
        position: fixed;
        top: 0;
        left: 0 !important;
        right: 0 !important;
        bottom: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        border-radius: 0;
        z-index: 10000;
      }

      .chuck-bubble {
        width: 56px;
        height: 56px;
        bottom: 16px;
      }

      .chuck-bubble.left {
        left: 16px;
      }

      .chuck-bubble.right {
        right: 16px;
      }

      .chuck-proactive-preview {
        bottom: 88px;
        width: calc(100vw - 32px);
      }

      .chuck-proactive-preview.left {
        left: 16px;
      }

      .chuck-proactive-preview.right {
        right: 16px;
      }

      .chuck-proactive-preview-card {
        min-height: 80px;
        grid-template-columns: 36px minmax(0, 1fr) 32px;
        gap: 10px;
        padding: 10px 6px 10px 10px;
      }

      .chuck-proactive-preview-avatar {
        width: 36px;
        height: 36px;
        font-size: 15px;
      }

      .chuck-proactive-preview-reply {
        min-height: 38px;
        padding: 7px 11px;
        font-size: 13px;
      }

      .chuck-bubble svg {
        width: 26px;
        height: 26px;
      }

      .chuck-header {
        padding: 14px 16px;
        padding-top: max(14px, env(safe-area-inset-top));
      }

      .chuck-header-title {
        font-size: 15px;
      }

      .chuck-header-subtitle {
        font-size: 11px;
      }

      .chuck-avatar {
        width: 36px;
        height: 36px;
      }

      .chuck-avatar-img,
      .chuck-avatar-initial {
        width: 36px;
        height: 36px;
        font-size: 16px;
      }

      .chuck-messages {
        padding: 12px;
        gap: 10px;
      }

      .chuck-message {
        max-width: 85%;
        padding: 10px 14px;
        font-size: 14px;
        border-radius: 14px;
      }

      .chuck-message.visitor {
        border-bottom-right-radius: 4px;
      }

      .chuck-message.agent {
        border-bottom-left-radius: 4px;
      }

      .chuck-input {
        padding: 10px 12px;
        padding-bottom: max(10px, env(safe-area-inset-bottom));
        gap: 8px;
      }

      .chuck-input-field {
        padding: 10px 14px;
        font-size: 16px; /* Prevents iOS zoom on focus */
      }

      .chuck-input-send {
        width: 42px;
        height: 42px;
      }

      .chuck-input-image {
        width: 42px;
        height: 42px;
      }

      .chuck-welcome {
        padding: 20px;
      }

      .chuck-welcome-icon {
        width: 44px;
        height: 44px;
      }

      .chuck-starters {
        gap: 6px;
        padding: 0 4px;
      }

      .chuck-starter-btn {
        padding: 7px 12px;
        font-size: 12px;
      }

      .chuck-powered {
        padding-bottom: max(8px, env(safe-area-inset-bottom));
      }

      /* Conversations list on mobile */
      .chuck-conversations {
        padding: 10px;
      }

      .chuck-new-conversation {
        padding: 12px 14px;
        font-size: 13px;
      }

      .chuck-conversation-item {
        padding: 10px 12px;
      }

      .chuck-conversation-text {
        font-size: 12px;
      }

      /* Image modal on mobile */
      .chuck-image-modal {
        padding: 10px;
      }

      .chuck-image-modal-content img {
        max-height: 75vh;
      }

      .chuck-image-modal-close {
        top: -35px;
      }

      /* Message images on mobile */
      .chuck-message-image img {
        max-width: 180px;
        max-height: 130px;
      }
    }

    /* Very small phones (up to 360px) */
    @media (max-width: 360px) {
      .chuck-header-info {
        gap: 8px;
      }

      .chuck-avatar {
        width: 32px;
        height: 32px;
      }

      .chuck-avatar-img,
      .chuck-avatar-initial {
        width: 32px;
        height: 32px;
        font-size: 14px;
      }

      .chuck-header-title {
        font-size: 14px;
      }

      .chuck-header-subtitle {
        font-size: 10px;
      }

      .chuck-message {
        max-width: 88%;
        padding: 8px 12px;
        font-size: 13px;
      }

      .chuck-input-field {
        padding: 8px 12px;
      }

      .chuck-input-send,
      .chuck-input-image {
        width: 38px;
        height: 38px;
      }
    }

    /* Short viewport height (landscape phones, small screens) */
    @media (max-height: 600px) {
      .chuck-window {
        height: calc(100vh - 80px);
        max-height: calc(100vh - 80px);
      }

      .chuck-header {
        padding: 10px 14px;
      }

      .chuck-messages {
        padding: 10px;
        gap: 8px;
      }

      .chuck-input {
        padding: 8px 12px;
      }

      .chuck-welcome {
        padding: 16px;
      }

      .chuck-powered {
        padding: 6px;
        font-size: 10px;
      }
    }

    /* Very short viewport (under 500px height) */
    @media (max-height: 500px) {
      .chuck-window {
        bottom: 70px;
        height: calc(100vh - 90px);
      }

      .chuck-bubble {
        width: 50px;
        height: 50px;
        bottom: 10px;
      }

      .chuck-bubble svg {
        width: 24px;
        height: 24px;
      }

      .chuck-header {
        padding: 8px 12px;
      }

      .chuck-avatar {
        width: 32px;
        height: 32px;
      }

      .chuck-avatar-img,
      .chuck-avatar-initial {
        width: 32px;
        height: 32px;
        font-size: 14px;
      }

      .chuck-welcome-icon {
        width: 36px;
        height: 36px;
        margin-bottom: 8px;
      }

      .chuck-welcome-text {
        font-size: 13px;
      }
    }

    /* Landscape mobile (short + narrow) */
    @media (max-height: 450px) and (max-width: 850px) {
      .chuck-window {
        position: fixed;
        top: 0;
        left: 0 !important;
        right: 0 !important;
        bottom: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        border-radius: 0;
      }

      .chuck-messages {
        padding: 8px;
      }

      .chuck-message {
        padding: 8px 10px;
        font-size: 12px;
      }
    }

    /* High DPI / Retina displays */
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
      .chuck-bubble {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      .chuck-window {
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      }
    }

    /* Prefers reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .chuck-window {
        animation: none;
      }

      .chuck-bubble {
        transition: none;
      }

      .chuck-proactive-preview {
        animation: none;
      }

      .chuck-typing-dot {
        animation: none;
        opacity: 0.7;
      }

      .chuck-online-indicator {
        animation: none;
      }

      .chuck-loading-spinner {
        animation: spin 2s linear infinite;
      }
    }
  `;
}
